/* ============================================================
   portfolio.js — Single source of truth for all content
   ============================================================ */

import splashAnimation from "./assets/lottie/splashAnimation";

// ── Splash Screen ────────────────────────────────────────────
const splashScreen = {
  enabled: true,
  animation: splashAnimation,
  duration: 1600
};

const illustration = {animated: false};

// ── Hero ─────────────────────────────────────────────────────
const greeting = {
  username: "Srimonchaari",
  name: "Srimonchaari Padmanabhan Babu",
  title: "Full-Stack Engineer, Applied AI Specialization",
  headline: "I build production software, with an AI specialization.",
  subline:
    "FastAPI and React applications shipped to production, extended with RAG pipelines, LLM evaluation, and observability tooling.",
  githubLink: "https://github.com/Srimonchaari",
  displayGreeting: true
};

// ── Impact Metrics ───────────────────────────────────────────
const impactMetrics = [
  {value: "72%", label: "Faster processing runtime (25 min → 7 min)"},
  {value: "6", label: "Production releases shipped, v1.0.0–v1.4.5"},
  {value: "93", label: "REST endpoints built for BHAAG AI"},
  {value: "85", label: "Pytest tests covering SensorSpeak's pipeline"},
  {value: "41.6pp", label: "Occlusion-robustness recovery in GC-ViT research"},
  {value: "12", label: "Backend API router areas built at Bosch"}
];

// ── Social Links ─────────────────────────────────────────────
const socialMediaLinks = {
  linkedin: "https://linkedin.com/in/Srimon",
  gmail: "srimonchaari@gmail.com",
  github: "https://github.com/Srimonchaari",
  display: true
};

// ── About ────────────────────────────────────────────────────
const aboutSection = {
  title: "About",
  paragraphs: [
    "I build Full-Stack software in production and extend it with Applied AI. At Robert Bosch GmbH, I built and shipped an internal analytics platform end-to-end: Python/FastAPI backend, React/TypeScript frontend, Entra ID authentication, and a Kubernetes delivery pipeline, replacing fragmented manual scripts with a self-service tool for process engineers.",
    "Outside work, that same build-it-end-to-end approach goes into AI systems: a multi-user SaaS with deterministic guardrails against LLM hallucination, a local RAG pipeline over sensor data with graceful degradation when the LLM is unavailable, and observability tooling that makes LLM-backed services measurable instead of opaque."
  ],
  currently:
    "AI Engineer (Working Student) at Robert Bosch GmbH · MSc in Artificial Intelligence at BTU Cottbus-Senftenberg.",
  workingStyle: [
    {
      title: "Ship the whole system, not just the model",
      body: "Every AI project I build includes the backend, the frontend, and the deployment path. A pipeline that only runs in a notebook doesn't count as done."
    },
    {
      title: "Constrain what the model is allowed to say",
      body: "LLMs left unconstrained will fabricate metrics and misattribute claims. I design deterministic verification layers around generation, not just prompts, so failure modes are caught in code."
    },
    {
      title: "Evaluation is a product requirement, not an afterthought",
      body: "If I can't measure whether the system is working, it isn't finished. Testing, benchmark design, and monitoring get the same rigour as the systems they cover."
    }
  ],
  display: true
};

// ── Capabilities ─────────────────────────────────────────────
const skillsSection = {
  title: "Capabilities",
  skillGroups: [
    {
      category: "Full-Stack Engineering",
      items: [
        "React 18",
        "TypeScript",
        "FastAPI",
        "SQLAlchemy",
        "REST API Design",
        "Vite"
      ]
    },
    {
      category: "Applied AI & LLM Systems",
      items: [
        "RAG Pipelines",
        "LlamaIndex",
        "Anthropic SDK",
        "Claim Verification",
        "LLM Evaluation",
        "Prompt Engineering"
      ]
    },
    {
      category: "DevOps & Delivery",
      items: ["Docker", "Kubernetes", "Helm", "Jenkins", "ArgoCD", "CI/CD"]
    },
    {
      category: "Auth & Security",
      items: [
        "Microsoft Entra ID",
        "OAuth 2.0 / OIDC",
        "JWT",
        "RBAC",
        "Per-user Data Isolation"
      ]
    },
    {
      category: "Observability",
      items: [
        "Prometheus",
        "Grafana",
        "PromQL",
        "Latency Profiling",
        "Metrics Instrumentation"
      ]
    },
    {
      category: "Data & ML",
      items: [
        "pandas",
        "NumPy",
        "scikit-learn",
        "PyTorch",
        "Statistical Testing"
      ]
    }
  ],
  display: true
};

// ── Education ────────────────────────────────────────────────
const educationInfo = {
  display: true,
  schools: [
    {
      schoolName: "Brandenburg University of Technology Cottbus-Senftenberg",
      logo: require("./assets/images/BTU.png"),
      subHeader: "MSc in Artificial Intelligence",
      duration: "Oct 2024 – Ongoing",
      desc: "Deep focus on ML theory, neural architectures, and distributed systems. Coursework and independent research applied to production AI deployment and evaluation methodology.",
      descBullets: []
    },
    {
      schoolName: "Sathyabama Institute of Science and Technology",
      logo: require("./assets/images/SIST.png"),
      subHeader: "BE in Computer Engineering",
      duration: "Jun 2020 – May 2024",
      desc: "Graduated with distinction. Built production projects in AI-embedded systems, OCR pipelines, and full-stack automation. Published research at ICCCAI 2024.",
      descBullets: []
    }
  ]
};

const techStack = {
  viewSkillBars: false,
  experience: [],
  displayCodersrank: false
};

// ── Work Experience ──────────────────────────────────────────
// Update experience here
const workExperiences = {
  display: true,
  experience: [
    {
      role: "AI Engineer, Working Student",
      company: "Robert Bosch GmbH",
      companylogo: require("./assets/images/bosch.png"),
      date: "Nov 2025 – Present",
      location: "Reutlingen, Germany",
      pmValue:
        "Process engineers get one self-service platform for wafer-defect investigation instead of fragmented manual scripts. Analysis that took 25 minutes now takes 7, and the tool ships through a fully automated Kubernetes pipeline.",
      metrics: [
        {value: "72%", label: "Faster runtime"},
        {value: "6", label: "Prod releases"},
        {value: "12", label: "API routers"}
      ],
      systemBuilt: [
        "Production internal analytics platform built end-to-end as primary developer: Python/FastAPI backend with 12 documented API router areas, React 18 + TypeScript SPA with 12 pages, and SQLAlchemy-backed data models",
        "Microsoft Entra ID OIDC authentication with JWT validation, role-based authorization, and delegated token forwarding",
        "Modular data-extraction pipeline and a 7-block statistical analysis engine (Fisher's exact test, Mann-Whitney U, Benjamini-Hochberg FDR, hierarchical clustering) with ranked Excel and Plotly outputs",
        "Full CI/CD and Kubernetes delivery path: Jenkins pipeline, Docker builds, Helm charts, and ArgoCD GitOps synchronization"
      ],
      impact: [
        "Replaced fragmented manual analysis scripts with one role-gated self-service application, shipped across 6 production releases (v1.0.0–v1.4.5)",
        "Reduced processing runtime from 25 minutes to 7 minutes (72%) through query partitioning, filtering, and deduplication",
        "Diagnosed and resolved query timeout, PVC multi-attach, timestamp-unit, and per-user data-isolation issues in production"
      ],
      tech: [
        "Python",
        "FastAPI",
        "React",
        "TypeScript",
        "SQLAlchemy",
        "Docker",
        "Kubernetes",
        "Jenkins",
        "ArgoCD"
      ]
    },
    {
      role: "AI Engineering Intern (Generative AI)",
      company: "Robert Bosch GmbH",
      companylogo: require("./assets/images/bosch.png"),
      date: "Apr 2025 – Oct 2025",
      location: "Reutlingen, Germany",
      pmValue:
        "Contributed to a Generative AI application that automated fact collection from multimodal data sources, supporting root-cause investigation with statistical and ML analysis components.",
      metrics: [],
      systemBuilt: [
        "Contributed to a Generative AI application for automated fact collection from multimodal data sources, using REST API-based workflows",
        "Implemented statistical-inference and machine-learning analysis components for root-cause investigation"
      ],
      impact: [
        "Supported automated fact collection from multimodal data sources",
        "Assisted with simulated anomaly-data preparation and investigated correlations and causal patterns"
      ],
      tech: ["Python", "REST APIs", "Statistical Inference", "Machine Learning"]
    },
    {
      role: "System Engineer",
      company: "Big Bang Boom Solutions",
      companylogo: require("./assets/images/bbbslogo.png"),
      date: "Nov 2023 – Sep 2024",
      location: "Chennai, India",
      pmValue:
        "Deployed a multi-sensor monitoring system unifying RF, infrared, and visual feeds into one interface, and kept headless field devices running through automated diagnostics.",
      metrics: [],
      systemBuilt: [
        "Multi-sensor fusion system integrating RF, infrared, and visual feeds, deployed through Docker Compose",
        "Unified computer-vision and infrared-identification workflows into a single monitoring interface",
        "C++ components for the sensor system, with development environments standardized via Docker Dev Containers"
      ],
      impact: [
        "Unified multiple sensor feeds across operational environments into one monitoring interface",
        "Automated service administration and hardware failure analysis on headless Jetson Nano devices using PowerShell and Bash"
      ],
      tech: [
        "C++",
        "Docker Compose",
        "Jetson Nano",
        "PowerShell",
        "Bash",
        "Linux"
      ]
    }
  ]
};

const openSource = {showGithubProfile: false, display: false};

// ── Engineering Work ──────────────────────────────────────────
const engineeringWork = {
  title: "Engineering Work",
  tagline: "Selected systems. Built to solve real problems.",
  profileLink: "https://github.com/Srimonchaari",
  username: "Srimonchaari",
  systems: [
    {
      name: "BHAAG AI",
      pmValue:
        "Job seekers get tailored, evidence-backed CVs instead of AI-generated documents that quietly fabricate metrics or misattribute experience. Every claim is checked against a candidate evidence model before and after generation.",
      problem:
        "Unconstrained LLM generation can fabricate experience, metrics, technologies, and employer attribution when writing job-application documents. Users need to trust that what gets generated on their behalf is true.",
      approach:
        "Built a deterministic analysis layer for requirement extraction, requirement matching, fit scoring, evidence ranking, and claim verification that runs before and after LLM generation. Wrapped it in a Full-Stack product with Flask, React, Google OAuth, SQLite (21 tables), SSE-based live generation progress, and pdflatex-compiled PDF output, deployed on Fly.io.",
      impact:
        "Every generated claim is constrained by verified candidate evidence; unsupported metrics fall back to safe wording or are excluded entirely. 93 REST endpoints, 77 regression test files, and 4 byte-for-byte golden-file tests protect generation behavior across releases.",
      metrics: [
        {value: "93", label: "REST endpoints"},
        {value: "77", label: "Regression tests"}
      ],
      tech: [
        "Python",
        "Flask",
        "React",
        "SQLite",
        "Anthropic SDK",
        "Google OAuth",
        "SSE",
        "Docker",
        "Fly.io"
      ],
      link: "https://bhaag-ai.fly.dev/",
      linkLabel: "Live Demo",
      featured: true
    },
    {
      name: "LLM Observability Dashboard",
      pmValue:
        "Engineering leads see simulated AI feature health in real time: latency, cost, and failure rates, in a stack that deploys in under a minute with no external API keys required.",
      problem:
        "LLM APIs ship with zero monitoring by default. Latency spikes, cost overruns, and silent failures go undetected until users report them.",
      approach:
        "Instrumented a FastAPI service with Prometheus counters and histograms labeled by provider, model, endpoint, and status. Built an auto-provisioned Grafana dashboard with PromQL queries for error rate, p95 latency, and token/cost usage. Packaged as a three-service Docker Compose stack with a deterministic simulated LLM traffic generator, so it runs with no external credentials.",
      impact:
        "A reproducible local monitoring environment where request volume, failures, latency, token usage, and estimated cost are visible without manual Grafana setup. A reusable instrumentation pattern for any LLM-backed service.",
      metrics: [
        {value: "<1 min", label: "Stack deployed"},
        {value: "p95", label: "Latency tracked"}
      ],
      tech: ["Python", "FastAPI", "Prometheus", "Grafana", "Docker"],
      link: "https://github.com/Srimonchaari/LLM-Observability",
      featured: false
    },
    {
      name: "SensorSpeak",
      pmValue:
        "Field engineers get plain-English motion event summaries from raw sensor CSVs, with no cloud dependency and no ML team ticket required.",
      problem:
        "MEMS accelerometer data is raw CSV. Identifying motion events required manually cross-referencing signal plots and timestamps: slow, inconsistent, and inaccessible to non-engineers.",
      approach:
        "Deterministic rule-based signal processing (rolling statistics, IQR outlier handling) classifies samples into motion events with severity tiers. Each event auto-generates a plain-English summary indexed by LlamaIndex with local HuggingFace embeddings (BAAI/bge-small-en-v1.5). A pluggable LLM layer (Ollama, OpenAI, or HuggingFace) answers natural-language questions over the event log, with a deterministic keyword fallback when no LLM is available.",
      impact:
        "Fully offline-capable pipeline: no cloud dependency required for core operation. 85 pytest unit tests cover the pipeline. Event classification stays deterministic and independent of the LLM, so answers remain grounded even without a model.",
      metrics: [
        {value: "85", label: "Pytest tests"},
        {value: "5", label: "Motion states"}
      ],
      tech: ["Python", "LlamaIndex", "Ollama", "FastAPI", "React", "pandas"],
      link: "https://github.com/Srimonchaari/SensorSpeak",
      featured: false
    },
    {
      name: "DFER GC-ViT Occlusion Robustness",
      pmValue:
        "Quantifies exactly how much a driver facial-expression model degrades when the mouth is occluded, and shows that targeted training recovers most of the loss without sacrificing clean-image accuracy.",
      problem:
        "A high-accuracy driver facial-expression recognition model had unknown robustness under partial face occlusion, such as masks or sunglasses, which are common in real driving conditions.",
      approach:
        "Reimplemented DFER-GCViT on a pretrained GC-ViT Base backbone (timm) with MTCNN face preprocessing on the KMU-FED near-infrared dataset. Built synthetic eye/mouth occlusion transforms and trained an occlusion-aware variant, evaluating both under 10-fold stratified cross-validation with CUDA AMP mixed precision. Built patch-based occlusion-sensitivity maps to localize which facial regions drive failures.",
      impact:
        "Baseline mouth-occlusion accuracy dropped 60.36 percentage points from clean (95.82%). Occlusion-aware training recovered 41.6 percentage points of that drop while costing only 2.37 points of clean accuracy.",
      metrics: [
        {value: "41.6pp", label: "Robustness recovered"},
        {value: "10-fold", label: "Cross-validation"}
      ],
      tech: [
        "Python",
        "PyTorch",
        "timm",
        "GC-ViT",
        "MTCNN",
        "OpenCV",
        "CUDA/AMP"
      ],
      link: "https://github.com/Srimonchaari/dfer-gcvit-occlusion-robustness",
      featured: false
    },
    {
      name: "CineAI",
      pmValue:
        "A hybrid recommendation pipeline evaluated under a realistic full-catalog ranking setup, not the inflated single-user-list evaluations common in course projects.",
      problem:
        "Building a movie recommendation pipeline over MovieLens-20M while evaluating ranking quality under a full-catalog retrieval setup, where most naive approaches collapse.",
      approach:
        "Contributed to a hybrid pipeline combining NCF/NeuMF (BPR pairwise ranking loss) and implicit ALS as collaborative signals, reranked with Sentence-BERT semantic similarity over TMDB metadata. Tracked Precision@10, Recall@10, NDCG@10, and RMSE across 20 evaluation runs, exposed through a Streamlit interface.",
      impact:
        "Reached Precision@10 of 0.2567 and NDCG@10 of 0.2698 under the project's full-catalog evaluation protocol, with RMSE of 0.7127.",
      metrics: [
        {value: "0.2567", label: "Precision@10"},
        {value: "0.7127", label: "RMSE"}
      ],
      tech: ["Python", "PyTorch", "Sentence-BERT", "implicit ALS", "Streamlit"],
      link: "https://github.com/AmanAsgola/Movie-Recommendation-System",
      featured: false
    }
  ],
  display: true
};

const githubSection = {
  title: "Engineering Work",
  username: "Srimonchaari",
  profileLink: "https://github.com/Srimonchaari",
  repos: [],
  display: false
};

// ── Academic & Additional Projects ─────────────────────────────
// Update projects here
const AcademicAndProfessionalProjects = {
  title: "More Projects",
  projects: [
    {
      projectName: "AI Learning Lab",
      problem:
        "Technical knowledge scattered across papers, docs, and repos leads to shallow understanding, with no system for structured, progressive deep-dives.",
      approach: [
        "Built a RAG pipeline that ingests content from multiple heterogeneous sources",
        "Extracted key concepts via structured extraction",
        "Generated curricula with daily units, checkpoints, and challenge sets"
      ],
      system: [
        "Retrieval pipeline over ingested source material",
        "Structured curriculum and challenge-set generation"
      ],
      results: [
        "Generates structured curricula, spaced learning units, and challenge sets from any knowledge corpus"
      ],
      tech: ["Python", "LLMs", "RAG"],
      githubLink: "https://github.com/Srimonchaari/ai-learning-lab-v1"
    },
    {
      projectName: "AI CSV Visualizer",
      problem:
        "Querying and visualizing structured data requires code, blocking non-technical stakeholders before they can ask the first question.",
      approach: [
        "Built a natural-language query layer over CSV files using LangChain agents",
        "Returned charts and plain-language summaries via a Streamlit UI"
      ],
      system: [
        "LangChain agent orchestration over tabular data",
        "Streamlit front end with zero configuration required"
      ],
      results: [
        "Zero-code data exploration for any structured dataset, accessible without engineering support"
      ],
      tech: ["Python", "LangChain", "Pandas", "Streamlit"],
      githubLink: "https://github.com/Srimonchaari/ai-csv-viz"
    },
    {
      projectName: "NCC Risk Detector",
      problem:
        "Risk signals buried in compliance records required slow, inconsistent manual review, with no scoring, prioritization, or audit trail.",
      approach: [
        "Built an ML classification pipeline with feature engineering and confidence scoring",
        "Exposed results via REST API for integration with reporting workflows"
      ],
      system: [
        "scikit-learn classification pipeline",
        "FastAPI service layer for downstream integration"
      ],
      results: [
        "End-to-end automation of manual review with confidence scores and REST-accessible results"
      ],
      tech: ["Python", "scikit-learn", "FastAPI", "Pandas"],
      githubLink: "https://github.com/Srimonchaari/ncc-risk-detector"
    },
    {
      projectName: "Prompt Enhancer",
      problem:
        "Prompts written in the flow of using an AI tool are rarely optimized, and switching tabs to a separate prompt-engineering tool breaks that flow.",
      approach: [
        "Built a Chrome Manifest V3 extension for in-flow prompt scoring",
        "Implemented a Reflect-then-Refine pipeline with native provider calls across multiple platforms"
      ],
      system: [
        "In-browser prompt scoring and refinement pipeline",
        "Framework-selectable refinement strategies"
      ],
      results: [
        "In-flow prompt optimization across 5 AI platforms without leaving the page"
      ],
      tech: ["JavaScript", "Chrome Extensions"],
      githubLink: "https://srimonchaari.github.io/Prompt_Optimiser/",
      linkLabel: "Live Demo"
    }
  ],
  display: true
};

// ── Publication ─────────────────────────────────────────────
const publicationSection = {
  title: "Publication",
  tagline: "Peer-reviewed research, published and awarded.",
  entry: {
    title:
      "Designing and Constructing a Versatile UAV System for Cleaning Applications",
    authors: "P.B. Srimonchaari, Dr. K. Ashokkumar, Dr. Albert Mayan J.",
    venue: "AIP Conference Proceedings, vol. 3257, no. 1, article 020163",
    publisher: "American Institute of Physics, 2025",
    award:
      "Best Paper Award, ICCCAI 2024 — in collaboration with Taylor's University, Malaysia",
    summary:
      "Designed a versatile UAV system architecture and mechanical design in SolidWorks, implemented GPS-based navigation, and validated the design through real flight testing for autonomous, cleaning-focused operation.",
    tech: ["Python", "GPS Navigation", "SolidWorks"],
    link: "https://pubs.aip.org/aip/acp/article-abstract/3257/1/020163/3351497/",
    linkLabel: "Read Paper"
  },
  display: true
};

// ── Certifications ────────────────────────────────────────────
// Add certifications here
const achievementSection = {
  title: "Certifications",
  achievementsCards: [
    {
      title: "Databricks Certified Generative AI Engineer Associate",
      issuer: "Databricks",
      year: "2025",
      subtitle:
        "Demonstrates production-grade expertise in RAG applications, LLM chain design, Vector Search, MLflow experiment tracking, and Model Serving on the Databricks Lakehouse platform.",
      image: require("./assets/images/databricks.png"),
      imageAlt: "Databricks Certification",
      footerLink: [
        {
          name: "View Credential",
          url: "https://credentials.databricks.com/98820b81-c260-405a-990b-be0943840922"
        }
      ]
    },
    {
      title: "Microsoft Certified: Azure AI Engineer Associate",
      issuer: "Microsoft",
      year: "2025",
      subtitle:
        "Validates ability to design and implement AI solutions on Azure, spanning NLP, computer vision, speech, and responsible AI principles aligned with production deployment standards.",
      image: require("./assets/images/Azure-Logo.jpg"),
      imageAlt: "Azure AI Certification",
      footerLink: [
        {
          name: "View Credential",
          url: "https://learn.microsoft.com/en-us/users/srimonchaari-7990/credentials/6b0d7bb5492817e5"
        }
      ]
    },
    {
      title: "Salesforce Agentforce Specialist",
      issuer: "Salesforce",
      year: "2025",
      subtitle:
        "Validates skills in designing, building, and deploying autonomous AI agents on the Salesforce Agentforce platform.",
      image: require("./assets/images/Salesforce.jpeg"),
      imageAlt: "Salesforce Agentforce Certification",
      footerLink: [
        {name: "Credential ID", url: "https://www.salesforce.com/trailblazer/"}
      ]
    }
  ],
  display: true
};

const resumeSection = {
  title: "Resume",
  subtitle: "Download my resume",
  display: false
};

// ── Contact ───────────────────────────────────────────────────
const contactInfo = {
  title: "Let's talk.",
  subtitle:
    "Open to Full-Stack and Applied AI engineering roles, research collaborations, and high-signal conversations about production software.",
  email_address: "srimonchaari@gmail.com",
  linkedin: "https://linkedin.com/in/Srimon",
  github: "https://github.com/Srimonchaari"
};

const isHireable = true;

export {
  illustration,
  greeting,
  impactMetrics,
  socialMediaLinks,
  aboutSection,
  splashScreen,
  skillsSection,
  educationInfo,
  techStack,
  workExperiences,
  engineeringWork,
  AcademicAndProfessionalProjects,
  publicationSection,
  githubSection,
  achievementSection,
  contactInfo,
  isHireable,
  openSource,
  resumeSection
};
